<script setup lang="ts">
import { onMounted, watch } from 'vue';
import BaseToast from './components/common/BaseToast.vue';
import GlobalDialogLayout from './layouts/GlobalDialogLayout.vue';
import SidebarLayout from './components/layout/SidebarLayout.vue';
import AuthLayout from './layouts/AuthLayout.vue';
import {
  isGlobalError,
  GlobalErrorMessage,
  isGlobalSucess,
  GlobalSucessMessage,
} from './refs/common.refs';
import { initAdminSocket } from './composables/useAdminSocket';
import { token } from './composables/useTokenCookie';

// Init socket as soon as a token is available (login may happen after mount)
watch(
  () => token.value?.accessToken,
  (t) => {
    if (t) initAdminSocket();
  },
  { immediate: true },
);

// Prefetch des chunks des routes les plus visitées dès que l'app est montée.
// requestIdleCallback garantit que ça ne bloque pas le rendu initial.
onMounted(() => {
  const prefetch = () => {
    import('./view/commerces/CommercesPage.vue');
    import('./view/clients/ClientsPage.vue');
    import('./view/commandes/CommandesPage.vue');
    import('./view/dashboard/DashboardPage.vue');
    import('./view/commerces/CommerceDetailPage.vue');
  };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(prefetch, { timeout: 3000 });
  } else {
    setTimeout(prefetch, 1000);
  }
});
</script>

<style>
.auth-fade-enter-active,
.auth-fade-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}
.auth-fade-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.auth-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.page-enter-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}
.page-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.page-enter-to {
  opacity: 1;
  transform: translateY(0);
}
</style>

<template>
  <RouterView v-slot="{ Component, route }">
    <!-- Rien avant que le router ait résolu la première route -->
    <template v-if="!route.name" />

    <!-- Layout auth : le shell est persistant, le composant est injecté via slot -->
    <template v-else-if="route.meta.layout === 'auth'">
      <AuthLayout>
        <Transition name="auth-fade" mode="out-in">
          <component :is="Component" :key="route.fullPath" />
        </Transition>
      </AuthLayout>
    </template>

    <!-- Layout app : sidebar -->
    <template v-else-if="route.meta.layout === 'app'">
      <SidebarLayout>
        <GlobalDialogLayout />
        <BaseToast
          :isError="isGlobalError"
          :errorMessage="GlobalErrorMessage"
          :isSucceed="isGlobalSucess"
          :succesMessage="GlobalSucessMessage"
        />
        <Transition name="page">
          <component :is="Component" :key="route.fullPath" />
        </Transition>
      </SidebarLayout>
    </template>

    <!-- Layout blank : 404, terms, etc. -->
    <template v-else>
      <BaseToast
        :isError="isGlobalError"
        :errorMessage="GlobalErrorMessage"
        :isSucceed="isGlobalSucess"
        :succesMessage="GlobalSucessMessage"
      />
      <Transition name="page">
        <component :is="Component" :key="route.fullPath" />
      </Transition>
    </template>
  </RouterView>
</template>
